import { api } from '../api/client'
import type { FirewallActivity, GpFailEntry, SystemFailEntry, ThreatEntry, UrlEntry } from '../api/types'
import { Card, EmptyState, ErrorBanner, Spinner, formatDateTime } from './common'
import { DataTable } from './DataTable'
import { useAsync } from '../hooks/useAsync'
import { t } from '../i18n/lingua'

/** Da kbps a una misura leggibile (Mbps/Gbps). */
function throughput(kbps?: number | null): string {
  if (kbps == null) return '—'
  if (kbps >= 1_000_000) return `${(kbps / 1_000_000).toFixed(2)} Gbps`
  if (kbps >= 1_000) return `${(kbps / 1_000).toFixed(1)} Mbps`
  return `${kbps} kbps`
}

/** Da byte a una misura leggibile. */
function formatBytes(n?: number | null): string {
  if (n == null) return '—'
  if (n >= 1e9) return `${(n / 1e9).toFixed(1)} GB`
  if (n >= 1e6) return `${(n / 1e6).toFixed(1)} MB`
  if (n >= 1e3) return `${(n / 1e3).toFixed(0)} KB`
  return `${n} B`
}

/** Il fornitore del firewall, leggibile. */
export function nomeFornitore(vendor?: string | null): string {
  if (vendor === 'fortinet') return 'Fortinet FortiGate'
  if (vendor === 'watchguard') return 'WatchGuard Firebox'
  if (vendor === 'paloalto') return 'Palo Alto'
  return '—'
}

/**
 * La nota sotto una classifica: sul Palo Alto è il report delle ultime 24 ore;
 * sul FortiGate (FortiOS 8 non espone più FortiView via API) è calcolata sulle
 * ultime righe di log, e lo si dice.
 */
function notaClassifica(d: FirewallActivity | null | undefined, predefinita: string): string {
  if (d?.basis != null) return t('Calcolata sulle ultime {n} righe di log del firewall.', { n: d.basis })
  return predefinita
}

function GravitaBadge({ sev }: { sev?: string | null }) {
  const s = (sev ?? '').toLowerCase()
  if (s === 'critical' || s === 'high') return <span className="badge badge--warn">{sev}</span>
  return <span className="badge">{sev ?? '—'}</span>
}

/**
 * Il cruscotto completo di un firewall (Palo Alto o FortiGate) — sistema, sessioni, VPN, admin,
 * top applicazioni/bloccate/minacce, minacce e URL recenti, accessi falliti —
 * per il firewall selezionato. Vive nella pagina Sicurezza → Firewall (non più
 * nella scheda del CI): il cruscotto è del firewall, non dell'host.
 */
export function FirewallDashboard({ firewallId }: { firewallId: string }) {
  return (
    <>
      <Cruscotto firewallId={firewallId} />
      <SchedaApp firewallId={firewallId} view="top_apps" title={t('Applicazioni più usate')}
        hint={t('Le applicazioni con più sessioni nelle ultime 24 ore.')} />
      <SchedaApp firewallId={firewallId} view="top_denied" title={t('Applicazioni più bloccate')}
        hint={t('Le applicazioni più negate dalle regole nelle ultime 24 ore.')} />
      <MinacceFrequenti firewallId={firewallId} />
      <Minacce firewallId={firewallId} />
      <UrlFiltering firewallId={firewallId} />
      <AccessiVpnFalliti firewallId={firewallId} />
      <AccessiSistemaFalliti firewallId={firewallId} />
    </>
  )
}

function Aggiorna({ onClick, loading }: { onClick: () => void; loading: boolean }) {
  return (
    <button className="secondary" onClick={onClick} disabled={loading}>
      {loading ? t('Interrogo…') : t('Aggiorna')}
    </button>
  )
}

function Cruscotto({ firewallId }: { firewallId: string }) {
  const dati = useAsync(() => api.firewallDashboard(firewallId), [firewallId])
  const d = dati.data
  return (
    <Card title={t('Cruscotto firewall')} actions={<Aggiorna onClick={() => dati.reload()} loading={dati.loading} />}>
      <ErrorBanner error={dati.error} />
      {dati.loading && !d ? (
        <Spinner label={t('Interrogo il firewall…')} />
      ) : !d ? null : !d.available ? (
        <p className="muted">{d.reason ?? t('Firewall non interrogabile.')}</p>
      ) : (
        <>
          <div className="ci-sommario">
            <span className="ci-sommario__item"><span className="ci-sommario__k">{t('Modello')}</span>{d.system?.model ?? '—'}</span>
            <span className="ci-sommario__item"><span className="ci-sommario__k">{t('Versione software')}</span>{d.system?.sw_version ?? '—'}</span>
            <span className="ci-sommario__item"><span className="ci-sommario__k">{t('Numero di serie')}</span><span className="mono">{d.system?.serial ?? '—'}</span></span>
            <span className="ci-sommario__item"><span className="ci-sommario__k">{t('Attivo da')}</span>{d.system?.uptime ?? '—'}</span>
            {d.system?.gp_client_version && (
              <span className="ci-sommario__item"><span className="ci-sommario__k">{t('Client GlobalProtect')}</span>{d.system.gp_client_version}</span>
            )}
            {d.system?.cpu_percent != null && (
              <span className="ci-sommario__item"><span className="ci-sommario__k">{t('CPU')}</span>{d.system.cpu_percent}%</span>
            )}
            {d.system?.memory_percent != null && (
              <span className="ci-sommario__item"><span className="ci-sommario__k">{t('Memoria')}</span>{d.system.memory_percent}%</span>
            )}
            {d.system?.ha_mode && (
              <span className="ci-sommario__item"><span className="ci-sommario__k">{t('Alta affidabilità')}</span>
                {d.system.ha_mode === 'standalone' ? t('nessuna (standalone)') : `${d.system.ha_mode} · ${t('{n} peer', { n: d.system.ha_peers ?? 0 })}`}
                {d.system.ha_role ? ` · ${t('questo nodo: {ruolo}', { ruolo: d.system.ha_role })}` : ''}
              </span>
            )}
            {d.system?.location && (
              <span className="ci-sommario__item"><span className="ci-sommario__k">{t('Sede')}</span>{d.system.location}</span>
            )}
          </div>
          {/* WatchGuard FireCluster: i membri con seriale, ruolo e modo — è qui
              che si vede quale dei due nodi sta rispondendo e quale è attivo. */}
          {(d.system?.ha_members?.length ?? 0) > 0 && (
            <p className="muted" style={{ margin: '6px 0 0', fontSize: 12 }}>
              {d.system!.ha_members!.map((m) => `${m.name ?? '?'} (${m.serial ?? '—'}): ${[m.role, m.mode].filter(Boolean).join(' ').toLowerCase()}${m.connection ? ` · ${m.connection}` : ''}`).join(' — ')}
            </p>
          )}
          <h4 style={{ margin: '14px 0 6px', fontSize: 14 }}>{t('Sessioni e traffico')}</h4>
          <div className="ci-sommario">
            <span className="ci-sommario__item"><span className="ci-sommario__k">{t('Sessioni attive')}</span>{d.sessions?.active ?? '—'}</span>
            <span className="ci-sommario__item"><span className="ci-sommario__k">{t('Nuove sessioni/s')}</span>{d.sessions?.cps ?? '—'}</span>
            {d.sessions?.kbps != null && (
              <span className="ci-sommario__item"><span className="ci-sommario__k">{t('Throughput')}</span>{throughput(d.sessions.kbps)}</span>
            )}
            {d.sessions?.tcp != null && (
              <span className="ci-sommario__item"><span className="ci-sommario__k">TCP / UDP / ICMP</span>{d.sessions.tcp} / {d.sessions.udp ?? '—'} / {d.sessions.icmp ?? '—'}</span>
            )}
          </div>

          <h4 style={{ margin: '14px 0 6px', fontSize: 14 }}>{t('Utenti VPN connessi')} ({(d.vpn_users ?? []).length})</h4>
          {(d.vpn_users ?? []).length === 0 ? (
            <p className="muted">{t('Nessun utente VPN connesso.')}</p>
          ) : (
            <DataTable rows={d.vpn_users ?? []} rowKey={(u, i) => `${u.username}-${i}`} columns={[
              { key: 'username', header: t('Utente'), value: (u) => u.username ?? '' },
              { key: 'computer', header: t('Dispositivo'), value: (u) => `${u.computer ?? ''} ${u.client ?? ''}`,
                render: (u) => <>{u.computer ?? '—'}{u.client ? <span className="muted"> · {u.client}</span> : null}</> },
              { key: 'virtual_ip', header: t('IP virtuale'), mono: true, value: (u) => u.virtual_ip ?? '' },
              { key: 'public_ip', header: t('IP pubblico'), mono: true, value: (u) => u.public_ip ?? '' },
              { key: 'tunnel_type', header: t('Tipo'), value: (u) => u.tunnel_type ?? '' },
              { key: 'login_time', header: t('Dal'), value: (u) => u.login_time ?? '' },
            ]} />
          )}

          <h4 style={{ margin: '14px 0 6px', fontSize: 14 }}>{t('Amministratori collegati')} ({(d.admins ?? []).length})</h4>
          {(d.admins ?? []).length === 0 ? (
            <p className="muted">{t('Nessun amministratore collegato.')}</p>
          ) : (
            <DataTable rows={d.admins ?? []} rowKey={(a, i) => `${a.admin}-${i}`} columns={[
              { key: 'admin', header: t('Amministratore'), value: (a) => a.admin ?? '',
                render: (a) => <>{a.admin ?? '—'}{a.current ? <span className="badge badge--ok"> {t('tu')}</span> : null}</> },
              { key: 'from', header: t('Da'), mono: true, value: (a) => a.from ?? '' },
              { key: 'type', header: t('Tipo'), value: (a) => a.type ?? '' },
              { key: 'idle_for', header: t('Inattivo da'), value: (a) => a.idle_for ?? '' },
            ]} />
          )}

          {d.queried_at && (
            <p className="field__hint">{t('Interrogato: {quando}', { quando: formatDateTime(d.queried_at) })}</p>
          )}
        </>
      )}
    </Card>
  )
}

function SchedaApp({ firewallId, view, title, hint }: {
  firewallId: string; view: 'top_apps' | 'top_denied'; title: string; hint: string
}) {
  const dati = useAsync(() => api.firewallActivity(view, firewallId), [firewallId, view])
  const d = dati.data
  return (
    <Card title={title} actions={<Aggiorna onClick={() => dati.reload()} loading={dati.loading} />}>
      <p className="field__hint">{notaClassifica(d, hint)}</p>
      <ErrorBanner error={dati.error} />
      {dati.loading && !d ? (
        <Spinner />
      ) : !d ? null : !d.available ? (
        <p className="muted">{d.reason ?? t('Firewall non interrogabile.')}</p>
      ) : !(d.apps ?? []).length ? (
        <p className="muted">{t('Nessun dato applicativo.')}</p>
      ) : (
        <DataTable rows={d.apps ?? []} initialSort={{ key: 'sessions', dir: 'desc' }} rowKey={(a, i) => `${a.name}-${i}`} columns={[
          { key: 'name', header: t('Applicazione'), value: (a) => a.name },
          { key: 'sessions', header: t('Sessioni'), value: (a) => a.sessions ?? 0, render: (a) => a.sessions?.toLocaleString() ?? '—' },
          { key: 'bytes', header: t('Byte'), value: (a) => a.bytes ?? 0, render: (a) => formatBytes(a.bytes) },
        ]} />
      )}
    </Card>
  )
}

function MinacceFrequenti({ firewallId }: { firewallId: string }) {
  const dati = useAsync(() => api.firewallActivity('top_attacks', firewallId), [firewallId])
  const d = dati.data
  return (
    <Card title={t('Minacce più frequenti')} actions={<Aggiorna onClick={() => dati.reload()} loading={dati.loading} />}>
      <p className="field__hint">{notaClassifica(d, t('Le minacce con più occorrenze nelle ultime 24 ore.'))}</p>
      <ErrorBanner error={dati.error} />
      {dati.loading && !d ? (<Spinner />) : !d ? null : !d.available ? (
        <p className="muted">{d.reason ?? t('Firewall non interrogabile.')}</p>
      ) : !(d.attacks ?? []).length ? (
        <p className="muted">{t('Nessuna minaccia nel periodo.')}</p>
      ) : (
        <DataTable rows={d.attacks ?? []} initialSort={{ key: 'count', dir: 'desc' }} rowKey={(a, i) => `${a.name}-${i}`} columns={[
          { key: 'name', header: t('Minaccia'), value: (a) => a.name },
          { key: 'subtype', header: t('Tipo'), value: (a) => a.subtype ?? '' },
          { key: 'count', header: t('Occorrenze'), value: (a) => a.count ?? 0, render: (a) => a.count?.toLocaleString() ?? '—' },
        ]} />
      )}
    </Card>
  )
}

function Minacce({ firewallId }: { firewallId: string }) {
  const dati = useAsync(() => api.firewallActivity('threat', firewallId), [firewallId])
  const d = dati.data
  const righe = (d?.entries ?? []) as ThreatEntry[]
  return (
    <Card title={t('Minacce recenti')} actions={<Aggiorna onClick={() => dati.reload()} loading={dati.loading} />}>
      <p className="field__hint">{t('Le ultime minacce rilevate dal firewall (antivirus, anti-spyware, IPS).')}</p>
      <ErrorBanner error={dati.error} />
      {dati.loading && !d ? (<Spinner />) : !d ? null : !d.available ? (
        <p className="muted">{d.reason ?? t('Firewall non interrogabile.')}</p>
      ) : !righe.length ? (
        <EmptyState title={t('Nessuna minaccia recente')} hint={t('Il firewall non ha minacce recenti da mostrare.')} />
      ) : (
        <DataTable rows={righe} initialSort={{ key: 'time', dir: 'desc' }} rowKey={(r, i) => `${r.name}-${i}`} columns={[
          { key: 'time', header: t('Ora'), mono: true, value: (r) => r.time ?? '' },
          { key: 'severity', header: t('Gravità'), value: (r) => r.severity ?? '', render: (r) => <GravitaBadge sev={r.severity} /> },
          { key: 'name', header: t('Minaccia'), value: (r) => r.name ?? '' },
          { key: 'src', header: t('Sorgente'), mono: true, value: (r) => r.src ?? '' },
          { key: 'dst', header: t('Destinazione'), mono: true, value: (r) => r.dst ?? '' },
          { key: 'action', header: t('Esito'), value: (r) => r.action ?? '' },
        ]} />
      )}
    </Card>
  )
}

function UrlFiltering({ firewallId }: { firewallId: string }) {
  const dati = useAsync(() => api.firewallActivity('url', firewallId), [firewallId])
  const d = dati.data
  const righe = (d?.entries ?? []) as UrlEntry[]
  return (
    <Card title={t('URL filtering')} actions={<Aggiorna onClick={() => dati.reload()} loading={dati.loading} />}>
      <p className="field__hint">{t('Le ultime richieste web con categoria ed esito (permesse o bloccate).')}</p>
      <ErrorBanner error={dati.error} />
      {dati.loading && !d ? (<Spinner />) : !d ? null : !d.available ? (
        <p className="muted">{d.reason ?? t('Firewall non interrogabile.')}</p>
      ) : !righe.length ? (
        <EmptyState title={t('Nessuna richiesta web recente')} hint={t('Il firewall non ha righe di URL filtering da mostrare.')} />
      ) : (
        <DataTable rows={righe} initialSort={{ key: 'time', dir: 'desc' }} rowKey={(r, i) => `${r.url}-${i}`} columns={[
          { key: 'time', header: t('Ora'), mono: true, value: (r) => r.time ?? '' },
          { key: 'action', header: t('Esito'), value: (r) => r.action ?? '',
            render: (r) => <span className={r.blocked ? 'badge badge--warn' : 'badge badge--ok'}>{r.action ?? '—'}</span> },
          { key: 'category', header: t('Categoria'), value: (r) => r.category ?? '' },
          { key: 'src', header: t('Sorgente'), mono: true, value: (r) => r.src ?? '' },
          { key: 'url', header: 'URL', mono: true, value: (r) => r.url ?? '' },
        ]} />
      )}
    </Card>
  )
}

function AccessiVpnFalliti({ firewallId }: { firewallId: string }) {
  const dati = useAsync(() => api.firewallActivity('gp_fail', firewallId), [firewallId])
  const d = dati.data
  const righe = (d?.entries ?? []) as GpFailEntry[]
  return (
    <Card title={t('Accessi VPN falliti')} actions={<Aggiorna onClick={() => dati.reload()} loading={dati.loading} />}>
      <p className="field__hint">{t('Gli ultimi tentativi di accesso VPN non riusciti (GlobalProtect, SSL VPN, IPsec).')}</p>
      <ErrorBanner error={dati.error} />
      {dati.loading && !d ? (<Spinner />) : !d ? null : !d.available ? (
        <p className="muted">{d.reason ?? t('Firewall non interrogabile.')}</p>
      ) : !righe.length ? (
        <EmptyState title={t('Nessun accesso VPN fallito')} hint={t('Nessun tentativo VPN fallito di recente.')} />
      ) : (
        <DataTable rows={righe} initialSort={{ key: 'time', dir: 'desc' }} rowKey={(r, i) => `${r.user}-${i}`} columns={[
          { key: 'time', header: t('Ora'), mono: true, value: (r) => r.time ?? '' },
          { key: 'user', header: t('Utente'), value: (r) => r.user ?? '' },
          { key: 'public_ip', header: t('IP pubblico'), mono: true, value: (r) => r.public_ip ?? '' },
          { key: 'client_os', header: t('Sistema'), value: (r) => r.client_os ?? '' },
          { key: 'location', header: t('Gateway/Portale'), value: (r) => r.location ?? '' },
          { key: 'error', header: t('Errore'), value: (r) => r.error ?? '' },
        ]} />
      )}
    </Card>
  )
}

function AccessiSistemaFalliti({ firewallId }: { firewallId: string }) {
  const dati = useAsync(() => api.firewallActivity('system_fail', firewallId), [firewallId])
  const d = dati.data
  const righe = (d?.entries ?? []) as SystemFailEntry[]
  return (
    <Card title={t('Accessi al sistema falliti')} actions={<Aggiorna onClick={() => dati.reload()} loading={dati.loading} />}>
      <p className="field__hint">{t('Gli ultimi accessi amministrativi al firewall non riusciti (autenticazione fallita).')}</p>
      <ErrorBanner error={dati.error} />
      {dati.loading && !d ? (<Spinner />) : !d ? null : !d.available ? (
        <p className="muted">{d.reason ?? t('Firewall non interrogabile.')}</p>
      ) : !righe.length ? (
        <EmptyState title={t('Nessun accesso al sistema fallito')} hint={t('Nessuna autenticazione amministrativa fallita di recente.')} />
      ) : (
        <DataTable rows={righe} initialSort={{ key: 'time', dir: 'desc' }} rowKey={(r, i) => `${r.time}-${i}`} columns={[
          { key: 'time', header: t('Ora'), mono: true, value: (r) => r.time ?? '' },
          { key: 'severity', header: t('Gravità'), value: (r) => r.severity ?? '', render: (r) => <GravitaBadge sev={r.severity} /> },
          { key: 'module', header: t('Modulo'), value: (r) => r.module ?? '' },
          { key: 'message', header: t('Messaggio'), value: (r) => r.message ?? '' },
        ]} />
      )}
    </Card>
  )
}
